import { apiClient, LONG_TIMEOUT_MS } from './client'
import type {
  CatalogDocumentsResponseDTO,
  DocumentDeleteResponseDTO,
  IngestResponseDTO,
  ReferenceType,
} from './types'

/**
 * Reference Library API surface (Step 8.0).
 *
 * The Reference Library reuses the existing ``/catalog/documents`` and
 * ``/ingest`` endpoints with ``document_kind='reference_document'``. The
 * dedicated client below exists so the feature module does not need to know
 * about the discriminator parameter — it just calls ``referenceLibraryApi.list()``
 * and gets the reference docs.
 *
 * Auth: cookie-only via ``credentials: 'include'`` (handled by ``apiClient``).
 * The frontend never reads or attaches tokens. ``tenantId`` is sourced from
 * the authenticated organization on the caller side; we never accept it from
 * untrusted UI state.
 *
 * Tenant isolation: ``enforce_tenant_match`` on the backend rejects any
 * request whose body/query ``tenant_id`` does not match the session-derived
 * tenant — independent of what the client sends.
 */

export interface ListReferenceDocumentsParams {
  tenantId: string
}

export interface UploadReferenceDocumentPayload {
  file: File
  tenantId: string
  documentId?: string | null
  /** Optional discriminated metadata for the Reference Library. */
  referenceType?: ReferenceType | null
  referenceDomain?: string | null
  referenceYear?: number | null
  referenceSource?: string | null
  title?: string | null
}

export interface DeleteReferenceDocumentPayload {
  tenantId: string
  documentId: string
}

export const referenceLibraryApi = {
  list: (params: ListReferenceDocumentsParams, signal?: AbortSignal) =>
    apiClient.get<CatalogDocumentsResponseDTO>(
      '/catalog/documents',
      {
        tenant_id: params.tenantId,
        document_kind: 'reference_document',
      },
      signal,
    ),

  upload: (payload: UploadReferenceDocumentPayload, signal?: AbortSignal) => {
    const form = new FormData()
    form.append('file', payload.file)
    form.append('tenant_id', payload.tenantId)
    form.append('document_kind', 'reference_document')
    if (payload.documentId) form.append('document_id', payload.documentId)
    if (payload.referenceType) form.append('reference_type', payload.referenceType)
    if (payload.referenceDomain) form.append('reference_domain', payload.referenceDomain)
    if (payload.referenceYear !== null && payload.referenceYear !== undefined) {
      form.append('reference_year', String(payload.referenceYear))
    }
    if (payload.referenceSource) form.append('reference_source', payload.referenceSource)
    if (payload.title) form.append('title', payload.title)
    // Reference ingestion runs embeddings too — same extended-but-bounded
    // ceiling as case-document uploads (see documentsApi.uploadDocument).
    return apiClient.postForm<IngestResponseDTO>('/ingest', form, {
      signal,
      timeoutMs: LONG_TIMEOUT_MS,
    })
  },

  delete: (payload: DeleteReferenceDocumentPayload, signal?: AbortSignal) => {
    const documentId = encodeURIComponent(payload.documentId)
    return apiClient.del<DocumentDeleteResponseDTO>(
      `/catalog/documents/${documentId}`,
      {
        query: { tenant_id: payload.tenantId },
        signal,
      },
    )
  },
}
